import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  School as SchoolIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { liveAnalyticsAPI } from '../../services/liveAnalyticsApi';
import StatCard from '../../components/Dashboard/StatCard';
import EnrollmentChart from '../../components/Dashboard/EnrollmentChart';
import DistrictOverview from '../../components/Dashboard/DistrictOverview';
import RecentActivity from '../../components/Dashboard/RecentActivity';
import DropoutAnalytics from '../../components/Dashboard/DropoutAnalytics';
import TeacherAttendanceSummary from '../../components/Dashboard/TeacherAttendanceSummary';
import AttendanceQuickSync from '../../components/Dashboard/AttendanceQuickSync';
import EducationLevelSummary from '../../components/Dashboard/EducationLevelSummary';
import EquityDashboard from '../EquityDashboard/EquityDashboard';

const Dashboard: React.FC = () => {
  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['liveDashboardStats'],
    queryFn: () => liveAnalyticsAPI.getLiveDashboardStats()
  });

  const stats = dashboardStats?.data || {};

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body1" color="text.secondary">
            Educational Management Information System - Live Data
          </Typography>
          <Chip 
            label="Real-time SIS Data" 
            color="success" 
            size="small" 
          />
        </Box>
      </Box>

      {/* Debug Component - Removed for production */}
      {/* <AuthDebug /> */}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Schools"
            value={stats.total_schools || 0}
            icon={<SchoolIcon />}
            color="#1976d2"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Students"
            value={stats.total_students || 0}
            icon={<PersonIcon />}
            color="#2e7d32"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Teachers"
            value={stats.total_teachers || 0}
            icon={<PersonIcon />}
            color="#ed6c02"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completion Rate"
            value={`${stats.graduation_rate || 0}%`}
            icon={<TrendingUpIcon />}
            color="#9c27b0"
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Enrollment by Year Chart */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Enrollment by Year
            </Typography>
            <EnrollmentChart />
          </Paper>
        </Grid>

        {/* Teacher Attendance Analytics */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <AttendanceQuickSync />
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, height: 300, overflow: 'auto' }}>
                <Typography variant="h6" gutterBottom>
                  Teacher Attendance Analytics
                </Typography>
                <Box sx={{ height: 250 }}>
                  <TeacherAttendanceSummary />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* District Overview */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Districts Overview
            </Typography>
            <DistrictOverview />
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <RecentActivity />
          </Paper>
        </Grid>

        {/* Education Level Summary */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <EducationLevelSummary />
          </Paper>
        </Grid>

        {/* Dropout Trend Analysis */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              AI-Powered Dropout Trend Analysis
            </Typography>
            <DropoutAnalytics />
          </Paper>
        </Grid>

        {/* District Equity Dashboard */}
        <Grid item xs={12}>
          <EquityDashboard />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;